import {observable, runInAction} from "mobx";
import api from "../../Services/Api";
import IHasIdAndName from "../../Entities/IHasIdAndName";
import uuidv4 from 'uuid/v4';
import {AutoCatalogByEntitiesDto} from "./Dtos";

export default class AutoCatalogSelectorStore {
    readonly id: string;
    @observable makes: IHasIdAndName[] = [];
    @observable make?: IHasIdAndName;

    @observable models: IHasIdAndName[] = [];
    @observable model?: IHasIdAndName;

    @observable generations: IHasIdAndName[] = [];
    @observable generation?: IHasIdAndName;

    @observable modifications: IHasIdAndName[] = [];
    @observable modification?: IHasIdAndName;

    @observable complectations: IHasIdAndName[] = [];
    @observable complectation?: IHasIdAndName;

    @observable bodies: BodyDto[] = [];
    @observable body?: BodyDto;

    @observable applied: boolean = false;
    @observable appliedCar?: AppliedCar;

    @observable hasChanges: boolean = false;
    onChanged: () => void;
    onApplied: () => void;

    constructor(onChanged: () => void, onApplied: () => void) {
        this.id = uuidv4();
        this.onChanged = onChanged;
        this.onApplied = onApplied;
    }

    async loadMakes() {
        const result = await api.get('autocatalog/makes');
        if (result.success) {
            this.makes = result.data;
        }
    }

    async setMake(makeId: string) {
        this.make = this.makes.find(c => c.id === makeId);
        await this.loadModels();
        runInAction(() => {
            this.model = undefined;
            this.generation = undefined;
            this.modification = undefined;
            this.complectation = undefined;
            this.body = undefined;
            this.change();
        })
    }

    get canLoadModels(): boolean {
        return this.make !== undefined && this.make !== null;
    }

    async loadModels() {
        if (!this.canLoadModels) {
            return;
        }

        const result = await api.get('autocatalog/models?makeId=' + this.make!.id);
        if (result.success) {
            this.models = result.data;
        }
    }

    async setModel(modelId: string) {
        this.model = this.models.find(c => c.id === modelId);
        await this.loadGenerations();

        runInAction(() => {
            this.generation = undefined;
            this.modification = undefined;
            this.complectation = undefined;
            this.body = undefined;
            this.change();
        })
    }

    get canLoadGenerations(): boolean {
        return this.model !== undefined && this.model !== null;
    }

    async loadGenerations() {
        if (!this.canLoadGenerations) {
            return;
        }

        const result = await api.get('autocatalog/generations?modelId=' + this.model!.id);
        if (result.success) {
            this.generations = result.data;
        }
    }

    async setGeneration(generationId: string) {
        this.generation = this.generations.find(c => c.id === generationId);
        await this.loadModifications();

        runInAction(() => {
            this.modification = undefined;
            this.complectation = undefined;
            this.body = undefined;
            this.change();
        });
    }

    get canLoadModifications(): boolean {
        return this.generation !== undefined && this.generation !== null;
    }

    async loadModifications() {
        if (!this.canLoadModifications) {
            return;
        }

        const result = await api.get('autocatalog/modifications?generationId=' + this.generation!.id);
        if (result.success) {
            this.modifications = result.data;
        }
    }

    async setModification(modificationId: string) {
        this.modification = this.modifications.find(c => c.id === modificationId);
        await this.loadComplectations();
        await this.loadBodies();
        runInAction(() => {
            this.complectation = undefined;
            this.body = undefined;
            this.change();
        });
    }

    get canLoadComplectations(): boolean {
        return this.modification !== undefined && this.modification !== null;
    }

    async loadComplectations() {
        if (!this.canLoadComplectations) {
            return;
        }

        const result = await api.get('autocatalog/complectations?modificationId=' + this.modification!.id);
        if (result.success) {
            this.complectations = result.data;
        }
    }

    setComplectation(complectations: string) {
        this.complectation = this.complectations.find(c => c.id === complectations);
        this.change();
    }

    get canLoadBodies(): boolean {
        return this.modification !== undefined && this.modification !== null;
    }

    async loadBodies() {
        if (!this.canLoadBodies) {
            return;
        }

        const result = await api.get('autocatalog/bodies?modificationId=' + this.modification!.id);
        if (result.success) {
            const response = result.data as BodyFromResponseDto[];
            this.bodies = response.map(x => ({
                id: x.typeId + '_' + x.doorsId,
                name: x.type + ' (дверей: ' + x.doors + ')',
                typeId: x.typeId,
                type: x.type,
                doorsId: x.doorsId,
                doors: x.doors
            } as BodyDto));
        }
    }

    setBody(bodyId: string) {
        this.body = this.bodies.find(c => c.id === bodyId);
        this.change();
    }

    get canApply(): boolean {
        return this.hasChanges
            && this.make !== undefined && this.make !== null
            && this.model !== undefined && this.model !== null
            && this.generation !== undefined && this.generation !== null;
    }

    private change() {
        this.hasChanges = true;
        this.onChanged();
    }

    apply() {
        if (!this.canApply) {
            return;
        }

        this.hasChanges = false;

        const make = {
            id: this.make!.id,
            name: this.make!.name,
        } as IHasIdAndName;
        const model = {
            id: this.model!.id,
            name: this.model!.name,
        } as IHasIdAndName;
        const generation = {
            id: this.generation!.id,
            name: this.generation!.name,
        } as IHasIdAndName;
        const modification = this.modification ? {
            id: this.modification!.id,
            name: this.modification!.name,
        } as IHasIdAndName : undefined;
        const complectation = this.complectation ? {
            id: this.complectation!.id,
            name: this.complectation!.name,
        } as IHasIdAndName : undefined;
        const body = this.body ? {
            id: this.body!.id,
            name: this.body!.name,
            typeId: this.body!.typeId,
            type: this.body!.type,
            doorsId: this.body!.doorsId,
            doors: this.body!.doors
        } as BodyDto : undefined;

        this.appliedCar = {
            make: make,
            model: model,
            generation: generation,
            modification: modification,
            complectation: complectation,
            body: body,
        } as AppliedCar;

        this.applied = true;
        this.onApplied();
    }

    async cancel() {
        if (!this.appliedCar) {
            return;
        }

        if (!this.make || this.make.id !== this.appliedCar.make.id) {
            await this.setMake(this.appliedCar.make.id);
        }

        if (!this.model || this.model.id !== this.appliedCar.model.id) {
            await this.setModel(this.appliedCar.model.id);
        }

        if (!this.generation || this.generation.id !== this.appliedCar.generation.id) {
            await this.setGeneration(this.appliedCar.generation.id);
        }

        if (this.appliedCar.modification && (!this.modification || this.modification.id !== this.appliedCar.modification.id)) {
            await this.setModification(this.appliedCar.modification.id);
        }

        if (this.appliedCar.complectation) {
            this.setComplectation(this.appliedCar.complectation.id);
        }

        if (this.appliedCar.body) {
            this.setBody(this.appliedCar.body.id);
        }

        this.hasChanges = false;
    }
    
    async init(dto: AutoCatalogByEntitiesDto) {
        await this.loadMakes();
        await this.setMake(dto.makeId);
        await this.setModel(dto.modelId);
        await this.setGeneration(dto.generationId);
        
        if (!dto.modificationId) {
            this.apply();
            return;
        }

        await this.setModification(dto.modificationId);
        
        if (dto.complectationId) {
            this.setComplectation(dto.complectationId);
        }
        
        if (dto.bodyTypeId && dto.bodyDoorsId) {
            this.setBody(dto.bodyTypeId + '_' + dto.bodyDoorsId);
        }

        this.apply();
    }

    get canCancel(): boolean {
        return this.applied && this.hasChanges;
    }
}

export interface BodyDto extends IHasIdAndName {
    typeId: number;
    type: string;
    doorsId: number;
    doors: number;
}

export interface BodyFromResponseDto {
    typeId: number;
    type: string;
    doors: number;
    doorsId: number;
}

interface AppliedCar {
    make: IHasIdAndName;
    model: IHasIdAndName;
    generation: IHasIdAndName;
    modification?: IHasIdAndName;
    complectation?: IHasIdAndName;
    body?: BodyDto;
}